<template>
  <BittsCollapse
    v-model:is-open="isOpen"
    chevron-placement="right"
    :toggle-on-body-click="false"
    aria-label="My Populations"
    class="c-integration-settings-own-populations"
  >
    <template #title>
      <div class="flex items justify-between w-full">
        <h6 class="text-info-text text-base/6 font-bold"> My Populations </h6>
        <p class="text-neutral-text-weak text-base/6 font-normal">{{
          isOpen ? 'Collapse' : 'Expand'
        }}</p>
      </div>
    </template>
    <div v-if="sortedPopulations.length === 0" class="flex pt-12 pl-16">
      <p class="text-neutral-text-weak text-base/6 font-normal mr-8">
        You don’t have any
        {{
          integrationProfileCrm ? friendlyNameMap[integrationProfileCrm] : ''
        }}
        Populations
      </p>
      <RouterLink to="/populations" class="text-secondary-accent text-base/6">
        <span>Create Population</span>
        <FontAwesomeIcon class="text-12 ml-4" :icon="['fak', 'open-link']" />
      </RouterLink>
    </div>
    <div class="flex flex-col gap-4 pt-12 pl-16">
      <IntegrationSettingsPopulationSegment
        v-if="hasSegmentGrouping"
        :populations="filteredPopulations"
        :checked-population-ids="checkedOwnPopIds"
        @population-checked="boxChecked"
        @populations-batch-checked="batchBoxChecked"
        :is-disabled="isDisabled"
      />
      <BittsCheckbox
        v-else
        v-for="population in sortedPopulations"
        :checked="checkedOwnPopIds.includes(population.id)"
        :disabled="isDisabled"
        :key="population.id"
        :label="population.name"
        @input="(isChecked: boolean) => boxChecked(population.id, isChecked)"
      />
    </div>
  </BittsCollapse>
</template>

<script setup lang="ts">
import { BittsCheckbox, BittsCollapse } from '@crossbeam/bitts';

import { sortBy } from 'lodash';
import { computed, ref } from 'vue';

import IntegrationSettingsPopulationSegment from '@/components/integrations/IntegrationSettingsPopulationSegment.vue';

import { FEATURE_FLAG } from '@/constants/feature_flags';
import { STANDARD_POPULATION_TYPES } from '@/constants/standard_populations';
import { useFeatureFlagStore, usePopulationsStore } from '@/stores';
import { FeedIntegrationType } from '@/types/feeds';
import {
  IntegrationProfileCrm,
  IntegrationProfileType,
  MinimalIntegrationProfile,
} from '@/types/integration_profiles';

const props = withDefaults(
  defineProps<{
    integrationProfileSettings?: MinimalIntegrationProfile | null;
    isDisabled?: boolean;
    settingType: IntegrationProfileType;
  }>(),
  { integrationProfileSettings: undefined, isDisabled: false },
);

const emit =
  defineEmits<
    (e: 'update:settings', payload: MinimalIntegrationProfile) => void
  >();

const isOpen = ref(true);

const featureFlagStore = useFeatureFlagStore();
const hasSegmentGrouping = featureFlagStore.hasFeatureFlag(
  FEATURE_FLAG.TEMP_UPDATED_DATA_PUSH_POP_TYPES,
);

const populationsStore = usePopulationsStore();
const checkedOwnPopIds = computed(() => {
  return (
    props.integrationProfileSettings?.population_settings
      .filter((pop) => pop.is_included && !pop.is_partner_population)
      .map((pop) => pop.population_id) ?? []
  );
});
const integrationProfileCrm = computed<IntegrationProfileCrm>(
  () =>
    (
      ({
        clari: 'salesforce',
        salesforce: 'salesforce',
        hubspot: 'hubspot',
        microsoft_dynamics: 'microsoft_dynamics',
        snowflake: undefined,
        crossbeam_copilot: undefined,
        webhook: undefined,
        api_filter: undefined,
        databricks: 'databricks',
      }) as const
    )[props.settingType],
);
const friendlyNameMap = {
  salesforce: 'Salesforce',
  microsoft_dynamics: 'Microsoft Dynamics',
  hubspot: 'HubSpot',
  snowflake: 'Snowflake',
  databricks: 'Databricks',
} as const;

const feedIntegrationType = computed<FeedIntegrationType | undefined>(() => {
  const typeMap: Record<
    IntegrationProfileType,
    FeedIntegrationType | undefined
  > = {
    clari: 'salesforce',
    salesforce: 'salesforce',
    hubspot: 'hubspot_v3',
    microsoft_dynamics: 'microsoft_dynamics',
    snowflake: undefined,
    crossbeam_copilot: undefined,
    webhook: undefined,
    api_filter: undefined,
    databricks: 'databricks',
  };
  return typeMap[props.settingType];
});

const filteredPopulations = computed(() => {
  if (!feedIntegrationType.value) {
    return populationsStore.populations;
  }
  return populationsStore.populations.filter(
    (pop) => pop.integration_type === feedIntegrationType.value,
  );
});

const sortedPopulations = computed(() => {
  return sortBy(
    filteredPopulations.value.map((pop) => ({
      id: pop.id,
      name: pop.name,
      standard_type: pop.standard_type,
    })),
    (pop) => {
      if (pop.standard_type)
        return STANDARD_POPULATION_TYPES.indexOf(pop.standard_type);
    },
  );
});

const updatePopulationSettings = (id: number, isChecked: boolean) => {
  return {
    // 1. map to the required format
    // 2. filter out items that are (is_included === false) and didn't exist in props.integrationProfileSettings initially
    population_settings: sortedPopulations.value
      .map((pop) => ({
        population_id: pop.id,
        is_included:
          pop.id === id ? isChecked : checkedOwnPopIds.value.includes(pop.id),
        is_partner_population: false,
        config_type: props.settingType,
      }))
      .filter(
        (pop) =>
          pop.is_included ||
          props.integrationProfileSettings?.population_settings.some(
            (setting) => setting.population_id === pop.population_id,
          ),
      ),
    standard_population_settings:
      props.integrationProfileSettings?.standard_population_settings ?? [],
  };
};

const boxChecked = (id: number, isChecked: boolean) => {
  const payload = updatePopulationSettings(id, isChecked);
  emit('update:settings', payload);
};

const batchBoxChecked = (ids: number[], isChecked: boolean) => {
  const currentChecked = new Set(checkedOwnPopIds.value);

  ids.forEach((id) =>
    isChecked ? currentChecked.add(id) : currentChecked.delete(id),
  );

  const existingSettings =
    props.integrationProfileSettings?.population_settings ?? [];

  const payload = {
    population_settings: sortedPopulations.value
      .map((pop) => ({
        population_id: pop.id,
        is_included: currentChecked.has(pop.id),
        is_partner_population: false,
        config_type: props.settingType,
      }))
      .filter(
        (pop) =>
          pop.is_included ||
          existingSettings.some((s) => s.population_id === pop.population_id),
      ),

    standard_population_settings:
      props.integrationProfileSettings?.standard_population_settings ?? [],
  };

  emit('update:settings', payload);
};
</script>

<style lang="pcss">
@reference "../../css/theme.css";
.c-integration-settings-own-populations {
  @apply px-12 py-8 bg-neutral-background-weak/50 rounded-lg;
}
</style>
